<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="header-content">
          <v-icon class="header-icon" size="32">mdi-cog</v-icon>
          <div class="header-text">
            <h3 class="header-title">Privacy & Settings</h3>
          </div>
        </div>

        <button @click="$emit('close')" class="btn-close" :disabled="updating">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>

      <div class="modal-body">
        <!-- Loading State -->
        <div v-if="loading" class="loading-screen">
          <div class="spinner"></div>
          <p class="loading-text">Loading settings...</p>
        </div>

        <div v-else>
          <!-- Message Privacy Settings -->
          <div class="settings-section">
            <div class="section-header">
              <v-icon class="section-icon" size="24">mdi-shield-lock-outline</v-icon>
              <h4 class="section-title">Message Privacy</h4>
            </div>
            <p class="section-description">
              Control who can send you messages
            </p>
            
            <div class="setting-card">
              <div class="setting-info">
                <label class="setting-label">Allow messages from everyone</label>
                <p class="setting-hint">
                  {{ privacySettings.allowMessagesFromAll 
                    ? 'Anyone can send you messages' 
                    : 'Only people you follow can message you' }}
                </p>
              </div>
              <div class="setting-control">
                <label class="toggle-switch">
                  <input 
                    type="checkbox" 
                    v-model="privacySettings.allowMessagesFromAll"
                    @change="updateMessagePermissions"
                  >
                  <span class="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          <!-- Blocked Users -->
          <div class="settings-section">
            <div class="section-header">
              <v-icon class="section-icon" size="24" color="error">mdi-cancel</v-icon>
              <h4 class="section-title">Blocked Users</h4>
              <span v-if="blockedUsers.length > 0" class="blocked-badge">
                {{ blockedUsers.length }}
              </span>
            </div>
            <p class="section-description">
              Blocked users cannot send you messages or see your activity
            </p>

            <!-- Block User Input -->
            <div class="block-user-section">
              <div class="search-wrapper">
                <v-icon class="search-icon" size="18">mdi-magnify</v-icon>
                <input 
                  v-model="blockUserQuery"
                  @input="searchUsersToBlock"
                  placeholder="Search users to block..."
                  class="search-input"
                />
              </div>

              <!-- Search Results -->
              <div v-if="searchResults.length > 0" class="search-results">
                <div class="results-header">
                  <v-icon left size="16">mdi-account-search</v-icon>
                  <span>Search Results ({{ searchResults.length }})</span>
                </div>
                <div class="users-list">
                  <div 
                    v-for="user in searchResults"
                    :key="user._id"
                    class="user-item"
                  >
                    <div class="user-info">
                      <div class="user-avatar">
                        {{ getUserInitials(user) }}
                      </div>
                      <div class="user-details">
                        <div class="user-name">{{ user.verse_username || 'Unknown' }}</div>
                      </div>
                    </div>
                    <v-btn
                      @click="blockUser(user._id, user.verse_username)"
                      variant="outlined"
                      color="error"
                      size="small"
                      prepend-icon="mdi-cancel"
                      class="btn-block"
                    >
                      Block
                    </v-btn>
                  </div>
                </div>
              </div>

              <!-- Search Hint -->
              <div v-if="blockUserQuery.length > 0 && blockUserQuery.length < 3" class="search-hint">
                <v-icon left size="16" color="warning">mdi-information</v-icon>
                <span>Type at least 3 characters to search</span>
              </div>
            </div>

            <!-- Blocked Users List -->
            <div v-if="blockedUsers.length > 0" class="blocked-users-list">
              <div class="blocked-header">
                <v-icon left size="16">mdi-account-cancel</v-icon>
                <span>Currently Blocked ({{ blockedUsers.length }})</span>
              </div>
              <div class="users-list">
                <div 
                  v-for="userId in blockedUsers"
                  :key="userId[0]"
                  class="user-item blocked"
                >
                  <div class="user-info">
                    <div class="user-avatar blocked">
                      {{ getUserInitials({ _id: userId[0], verse_username: userId[1] }) }}
                    </div>
                    <div class="user-details">
                      <div class="user-name">{{ getUserName(userId[1]) }}</div>
                      <div class="user-status">Blocked</div>
                    </div>
                  </div>
                  <v-btn
                    @click="unblockUser(userId[0])"
                    variant="outlined"
                    color="success"
                    size="small"
                    prepend-icon="mdi-check"
                    class="btn-unblock"
                  >
                    Unblock
                  </v-btn>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else class="empty-state">
              <v-icon size="48" color="grey">mdi-cancel</v-icon>
              <p class="empty-text">No blocked users</p>
            </div>
          </div>

          <!-- Account Information -->
          <div class="settings-section">
            <div class="section-header">
              <v-icon class="section-icon" size="24">mdi-account-circle-outline</v-icon>
              <h4 class="section-title">Account Information</h4>
            </div>
            
            <div class="info-card">
              <div class="info-item">
                <div class="info-label">
                  <v-icon left size="18">mdi-email-outline</v-icon>
                  <span>Email</span>
                </div>
                <div class="info-value">{{ currentUser.email }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">
                  <v-icon left size="18">mdi-account-outline</v-icon>
                  <span>Username</span>
                </div>
                <div class="info-value">{{ currentUser.verse_username }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">
                  <v-icon left size="18">mdi-circle</v-icon>
                  <span>Status</span>
                </div>
                <div class="info-value">
                  <span :class="{ online: isOnline }" class="status-indicator">
                    <span class="status-dot"></span>
                    {{ isOnline ? 'Online' : 'Offline' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Data Management -->
          <div class="settings-section">
            <div class="section-header">
              <v-icon class="section-icon" size="24">mdi-database-cog-outline</v-icon>
              <h4 class="section-title">Data Management</h4>
            </div>
            
            <div class="data-actions">
              <v-btn
                @click="clearChatHistory"
                variant="outlined"
                color="error"
                prepend-icon="mdi-delete-sweep"
                block
                class="action-btn"
              >
                Clear All Chat History
              </v-btn>
            </div>
          </div>

          <!-- About -->
          <div class="settings-section last">
            <div class="section-header">
              <v-icon class="section-icon" size="24">mdi-information-outline</v-icon>
              <h4 class="section-title">About</h4>
            </div>
            <div class="about-card">
              <div class="about-item">
                <v-icon left size="20" color="primary">mdi-application</v-icon>
                <div>
                  <strong>Muzible Chat</strong>
                  <span class="version">v1.1.0</span>
                </div>
              </div>
              <div class="about-description">
                Secure, real-time messaging platform
              </div>
              <div class="about-links">
                <a href="#" @click.prevent="showPrivacyPolicy">
                  <v-icon left size="16">mdi-shield-check</v-icon>
                  Privacy Policy
                </a>
                <span class="separator">•</span>
                <a href="#" @click.prevent="showTerms">
                  <v-icon left size="16">mdi-file-document</v-icon>
                  Terms of Service
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Error Messages -->
        <div v-if="error" class="error-message">
          <v-icon left size="20" color="error">mdi-alert-circle</v-icon>
          <span>{{ error }}</span>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer">
        <v-btn
          @click="$emit('close')"
          variant="outlined"
          prepend-icon="mdi-close"
          class="footer-btn"
        >
          Close
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import chatService from '@/services/chat/ChatService'
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useMobile } from '@/composables/useMobile.js'
import { useMobileXs } from '@/composables/useMobileXs.js'

export default {
  setup() {
    const isMobile = useMobile()
    const isMobileXs = useMobileXs()

    const notify = (message) => {
      toast(message, {
        theme: "dark",
        type: "success",
        position: "bottom-center",
        closeOnClick: false,
        pauseOnHover: false,
        autoClose: 1500,
        transition: "flip",
        dangerouslyHTMLString: true,
      });
    };

    const notifyError = (message) => {
      toast(message, {
        theme: "dark",
        type: "error",
        position: "bottom-center",
        closeOnClick: false,
        pauseOnHover: false,
        autoClose: 3000,
        transition: "flip",
        dangerouslyHTMLString: true,
      });
    };

    const notifyWarning = (message) => {
      toast(message, {
        theme: "dark",
        type: "warning",
        position: "bottom-center",
        closeOnClick: false,
        pauseOnHover: false,
        autoClose: 3000,
        transition: "flip",
        dangerouslyHTMLString: true,
      });
    };

    return { isMobile, isMobileXs, notify, notifyError, notifyWarning }
  },
  name: 'SettingsModal',
  emits: ['close'],
  data() {
    return {
      loading: true,
      updating: false,
      updateMessage: '',
      error: null,
      
      // Privacy settings
      privacySettings: {
        allowMessagesFromAll: true,
        blockedUsers: [],
        following: []
      },
      
      // Blocked users
      blockedUsers: [],
      blockUserQuery: '',
      searchResults: [],
      searchTimeout: null,
      
      // User info
      currentUser: {
        email: '',
        verse_username: '',
        id: ''
      },
      isOnline: false
    }
  },
  async mounted() {
    await this.loadSettings()
  },

  beforeUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = null
    }
  },

  methods: {
    closeModal() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
        this.searchTimeout = null
      }
      this.$emit('close')
    },

    async loadSettings() {
      try {
        this.loading = true
        this.error = null

        const privacy = await chatService.getPrivacySettings()
        this.privacySettings = privacy
        this.blockedUsers = privacy.blockedUsers || []

        const status = await chatService.getMyStatus()
        this.isOnline = status.isOnline

        this.currentUser = {
          email: this.$store.getters.getUserData.email || 'user@muzible.com',
          verse_username: this.$store.getters.getUserData.verse_username || 'username',
          id: this.$store.getters.getUserData.id || 'user ID'
        }

      } catch (error) {
        this.error = 'Failed to load settings'
      } finally {
        this.loading = false
      }
    },

    async updateMessagePermissions() {
      try {
        this.updating = true
        this.updateMessage = 'Updating privacy settings...'
        await chatService.updateMessagePermissions(this.privacySettings.allowMessagesFromAll)
        this.showToast('Privacy settings updated', 'success')
      } catch (error) {
        this.error = 'Failed to update privacy settings'
        this.privacySettings.allowMessagesFromAll = !this.privacySettings.allowMessagesFromAll
      } finally {
        this.updating = false
      }
    },

    async searchUsersToBlock() {
      if (this.blockUserQuery.length < 3) {
        this.searchResults = []
        return
      }

      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(async () => {
        try {
          chatService.usersSearch(this.blockUserQuery.trim())
            .then(response => {
              this.searchResults = response.filter(user =>
                !this.blockedUsers.some(blocked => blocked[0] === user._id) && user._id !== this.currentUser.id
              )
            })
            .catch(error => {
              this.error = 'Failed to search users'
              this.searchResults = []
            })
        } catch (error) {
          this.searchResults = []
        }
      }, 300)
    },

    async blockUser(userId, verse_username) {
      try {
        this.updating = true
        this.updateMessage = 'Blocking user...'

        await chatService.blockUser(userId)
        
        this.blockedUsers.push([userId, verse_username])
        this.searchResults = this.searchResults.filter(u => u._id !== userId)
        this.blockUserQuery = ''

        this.showToast('User blocked successfully', 'success')

      } catch (error) {
        this.showToast('Failed to block user', 'error')
      } finally {
        this.updating = false
      }
    },

    async unblockUser(userId) {
      try {
        this.updating = true
        this.updateMessage = 'Unblocking user...'

        await chatService.unblockUser(userId)
        
        this.blockedUsers = this.blockedUsers.filter(blocked => blocked[0] !== userId)

        this.showToast('User unblocked successfully', 'success')

      } catch (error) {
        this.showToast('Failed to unblock user', 'error')
      } finally {
        this.updating = false
      }
    },

    async clearChatHistory() {
      try {
        this.updating = true
        this.updateMessage = 'Clearing chat history...'

        await chatService.clearAllChatHistory()

        this.showToast('Chat history cleared', 'success')

      } catch (error) {
        this.showToast('Failed to clear chat history', 'error')
      } finally {
        this.updating = false
      }
    },

    showPrivacyPolicy() {
      window.open('https://muzible.com/privacy', '_blank')
    },

    showTerms() {
      window.open('https://muzible.com/terms', '_blank')
    },

    getUserInitials(user) {
      if (!user) return '??'
      const name = user.verse_username || user.username || user._id
      return name.substring(0, 2).toUpperCase()
    },

    getUserName(verse_username) {
      return `${verse_username}`
    },

    showToast(message, type = 'info') {
      if (type === 'error') {
        this.notifyError(message)
      } else if (type === 'warning'){
        this.notifyWarning(message)
      } else {
        this.notify(message)
      }
    },
  }
}
</script>

<style lang="scss" scoped>
@use '../../views/css/styles.scss';

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.3s ease;
}

/* Modal Content */
.modal-content {
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.98), rgba(18, 18, 18, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease;
  position: relative;
}
/* Modal Header */
.modal-header {
  padding: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(0, 0, 0, 0.3);
  transition: background 0.3s ease;
}

.modal-header.updating {
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.3), rgba(0, 86, 179, 0.3));
  border-left: 3px solid #007bff;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.header-text {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.header-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  animation: slideInLeft 0.3s ease;
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.header-icon {
  color: white !important;
  flex-shrink: 0;
}

.modal-header.updating .header-icon {
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.header-title {
  margin: 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.loading-text {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.btn-close {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: white;
  flex-shrink: 0;
}

.btn-close:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.4);
}

.btn-close:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}



/* Modal Body */
.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: transparent;
}

.modal-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
}

/* Loading Screen */
.loading-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.loading-text {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 16px;
  margin-bottom: 0;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Settings Section */
.settings-section {
  margin-bottom: 28px;
  padding-bottom: 28px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  animation: slideIn 0.3s ease;
}

.settings-section.last {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.section-icon {
  color: white !important;
}

.section-title {
  margin: 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: white;
  flex: 1;
}

.blocked-badge {
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 12px;
  font-family: "Darker Grotesque", sans-serif;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.4);
}

.section-description {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 16px 0;
  line-height: 1.5;
}

/* Setting Card */
.setting-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
}

.setting-card:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.25);
}

.setting-info {
  flex: 1;
}

.setting-label {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: white;
  display: block;
  margin-bottom: 6px;
}

.setting-hint {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
}

.setting-control {
  flex-shrink: 0;
}

/* Toggle Switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: 0.3s;
  border-radius: 28px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 4px;
  bottom: 3px;
  background: white;
  transition: 0.3s;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

input:checked + .toggle-slider {
  background: linear-gradient(135deg, #007bff, #0056b3);
  border-color: rgba(0, 123, 255, 0.5);
}

input:checked + .toggle-slider:before {
  transform: translateX(22px);
}

/* Search Wrapper */
.search-wrapper {
  position: relative;
  margin-bottom: 16px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 40px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  color: white;
  outline: none;
  transition: all 0.3s ease;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.search-input:focus {
  border-color: rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.08);
}

/* Search Results / Blocked Users List */
.search-results,
.blocked-users-list {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  overflow: hidden;
  animation: slideIn 0.3s ease;
}

.results-header,
.blocked-header {
  padding: 10px 16px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-family: "Darker Grotesque", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  gap: 8px;
}

.users-list {
  max-height: 300px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.users-list::-webkit-scrollbar {
  width: 6px;
}

.users-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
}

/* User Item */
.user-item {
  padding: 14px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.2s ease;
}

.user-item:last-child {
  border-bottom: none;
}

.user-item:hover {
  background: rgba(0, 123, 255, 0.1);
}

.user-item.blocked {
  background: rgba(220, 53, 69, 0.05);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #007bff, #0056b3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  font-weight: 700;
  font-family: "Darker Grotesque", sans-serif;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.user-avatar.blocked {
  background: linear-gradient(135deg, #6c757d, #545b62);
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-status {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

.btn-block,
.btn-unblock {
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
}

/* Search Hint */
.search-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 10px;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.empty-text {
  margin: 16px 0 0 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.6);
}

/* Info Card */
.info-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.info-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
}

.info-value {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  color: white;
  font-weight: 500;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #dc3545;
  animation: pulse 2s ease-in-out infinite;
}

.status-indicator.online .status-dot {
  background: #28a745;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Data Actions */
.data-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.action-btn {
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  text-transform: none !important;
}

/* About Card */
.about-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 16px;
}

.about-item {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  div {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: "Darker Grotesque", sans-serif;
    font-size: 16px;
    color: white;

    strong {
      font-weight: 700;
    }
  }
}

.version {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500 !important;
}

.about-description {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 16px;
  line-height: 1.6;
}

.about-links {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;

  a {
    color: #007bff;
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s ease;

    &:hover {
      color: #0056b3;
      text-decoration: underline;
    }
  }

  .separator {
    color: rgba(255, 255, 255, 0.3);
  }
}

/* Error Message */
.error-message {
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.4);
  border-radius: 10px;
  padding: 12px 16px;
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  color: #dc3545;
  animation: shake 0.3s ease;
}

@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-5px);
  }
  75% {
    transform: translateX(5px);
  }
}

/* Modal Footer */
.modal-footer {
  padding: 20px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.3);
}

.footer-btn {
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
  min-width: 120px !important;
}

.footer-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    max-height: 90vh;
  }

  .modal-header {
    padding: 20px;
  }

  .header-title {
    font-size: 20px;
  }

  .modal-body {
    padding: 16px 20px;
  }

  .section-title {
    font-size: 18px;
  }

  .setting-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .setting-control {
    align-self: flex-end;
  }

  .modal-footer {
    padding: 16px 20px;

    .footer-btn {
      width: 100%;
    }
  }
}

/* Vuetify Overrides */
:deep(.v-btn:hover) > .v-btn__overlay {
  opacity: 0.1;
}
</style>